import {parseISO, isAfter, isBefore, isEqual, startOfToday} from 'date-fns';
import {storeToRefs} from 'pinia';
import * as Sentry from '@sentry/nuxt';
import {
	type Gsoa_CustomerProfile_CRMGCustomer as CRMGCustomer,
	type Gsoa_CustomerProfile_FoundReceipts as FoundReceipts,
	type Gsoa_CustomerProfile_CustomerUpdateParams as CustomerUpdateParams,
	GsoaCustomerService,
	GsoaReceiptsService,
	GsoaCouponsService,
	type Gsoa_Coupons_GetCustomerCoupons_response as GetCustomerCoupons,
	type Gsoa_Coupons_GetCustomerCoupon_response as CustomerCoupon,
	type Gsoa_Coupons_SetCouponCustomer as SetCouponCustomer,
	type CustomerCouponGiftsResponse as CustomerCouponGifts,
	type GiftLevelModel as GiftLevel,
	type CancelablePromise,
} from '../openapi';
import {useStoryblokHypermarketsStore} from './storyblokHypermarkets';
import {CouponType, type GiftWithType} from '~/types/coupon';
import useThrottledFetchingWithCache from '~/composables/useThrottledFetchingWithCache';

type CustomerCoupons = GetCustomerCoupons & {
	totalCount: number;
};

export const useCustomerProfileStore = defineStore('customerProfile', () => {
	const profile = ref<CRMGCustomer | null>(null);
	const receipts = ref<FoundReceipts | null>(null);
	const cache = useThrottledFetchingWithCache<GetCustomerCoupons | null>({validity: 1000});
	const coupons = ref<CustomerCoupons | null>(null);
	const allGifts = ref<CustomerCouponGifts | null>(null);
	const giftLevels = ref<GiftLevel[] | null>(null);
	const giftLevelsNoAuthorization = ref<GiftLevel[] | null>(null);
	const giftLevelsFetchedAuthorized = ref(false);
	const reqHeaders = useRequestHeaders(['x-forwarded-for']);

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarketInstance} = storeToRefs(hypermarketsStore);

	const {isAuthenticated} = useAuth();
	const userIsLogged = computed(() => !!profile.value);

	const giftsWithType = computed(() => {
		const activeGifts = mapGiftsByBucket(allGifts.value?.active, 'active');
		const pendingGifts = mapGiftsByBucket(allGifts.value?.pending, 'pending').sort(giftLevelComparator);
		const usedGifts = mapGiftsByBucket(allGifts.value?.used, 'used');
		const lastQuarterActiveGifts = mapGiftsByBucket(allGifts.value?.lastQuarter?.active, 'active');
		const lastQuarterUsedGifts = mapGiftsByBucket(allGifts.value?.lastQuarter?.used, 'used');

		return {
			active: [...activeGifts, ...lastQuarterActiveGifts].sort(giftLevelComparator),
			pending: pendingGifts,
			used: [...usedGifts, ...lastQuarterUsedGifts].sort(giftLevelComparator),
		};
	});
	const flatGifts = computed(() => {
		return Object.values(giftsWithType.value).flat();
	});

	function getGiftLevelOrder(gift: GiftWithType): number {
		const levelNumber = Number(getGiftLevelForCoupon(gift.coupon.Id)?.levelNumber);
		return Number.isFinite(levelNumber) ? levelNumber : Number.MAX_SAFE_INTEGER;
	}

	function giftLevelComparator(a: GiftWithType, b: GiftWithType): number {
		return getGiftLevelOrder(a) - getGiftLevelOrder(b);
	}

	const filteredCoupons = computed(() => {
		const today = startOfToday();

		// Filter lokalně přiřazené kupóny podle vybraného hypermarketu
		const filteredLocaleCoupons = coupons.value?.List?.filter((coupon) => {
			return canUseCouponInHypermarket(coupon, Number(selectedHypermarketInstance.value?.content.gsoaId));
		}) ?? [];

		const couponsList = filteredLocaleCoupons;

		// 1. Platné kupóny: valid_from ≤ dnes ≤ valid_to && Used === 0
		const validCoupons = couponsList
			.filter((coupon) => {
				const validFrom = parseISO(coupon.ValidFrom);
				const validTo = parseISO(coupon.ValidTo);
				return (
					getCouponType(coupon) !== CouponType.USED
                && (isBefore(validFrom, today) || isEqual(validFrom, today))
                && (isAfter(validTo, today) || isEqual(validTo, today))
				);
			})
			.sort((a, b) => {
				// Priority ASC
				if ((a.Priority ?? 0) !== (b.Priority ?? 0)) {
					return (a.Priority ?? 0) - (b.Priority ?? 0);
				}
				// ValidFrom DESC
				const aValidFrom = parseISO(a.ValidFrom).getTime();
				const bValidFrom = parseISO(b.ValidFrom).getTime();
				if (aValidFrom !== bValidFrom) {
					return bValidFrom - aValidFrom;
				}
				// Id DESC
				return b.Id - a.Id;
			});

		// 2. Budoucí kupóny: valid_from > dnes && Used === 0
		const futureCoupons = couponsList
			.filter((coupon) => {
				const validFrom = parseISO(coupon.ValidFrom);
				return getCouponType(coupon) !== CouponType.USED && isAfter(validFrom, today);
			})
			.sort((a, b) => {
				// Priority ASC
				if ((a.Priority ?? 0) !== (b.Priority ?? 0)) {
					return (a.Priority ?? 0) - (b.Priority ?? 0);
				}
				// ValidFrom DESC
				const aValidFrom = parseISO(a.ValidFrom).getTime();
				const bValidFrom = parseISO(b.ValidFrom).getTime();
				if (aValidFrom !== bValidFrom) {
					return bValidFrom - aValidFrom;
				}
				// Id DESC
				return b.Id - a.Id;
			});

		// 3. Uplatněné jednorázové kupóny: Used === 2
		const usedCoupons = couponsList
			.filter((coupon) => getCouponType(coupon) === CouponType.USED)
			.sort((a, b) => {
				// Priority ASC
				if ((a.Priority ?? 0) !== (b.Priority ?? 0)) {
					return (a.Priority ?? 0) - (b.Priority ?? 0);
				}
				// ValidFrom DESC
				const aValidFrom = parseISO(a.ValidFrom).getTime();
				const bValidFrom = parseISO(b.ValidFrom).getTime();
				if (aValidFrom !== bValidFrom) {
					return bValidFrom - aValidFrom;
				}
				// Id DESC
				return b.Id - a.Id;
			});

		return [
			...validCoupons,
			...futureCoupons,
			...usedCoupons,
		].filter((coupon) => getCouponType(coupon));
	});

	const filteredCouponsCount = computed(() => filteredCoupons.value?.length);
	const couponsAndGiftsCount = computed(() => Number(filteredCoupons.value?.length) + Number(flatGifts.value?.length));

	// Synchronize Sentry user with the profile
	watch(
		profile,
		(newProfile) => {
			if (!newProfile) {
				Sentry.setUser(null);
				return;
			}

			Sentry.setUser({
				id: newProfile.registration?.customerID,
				email: newProfile.contacts?.email ?? undefined,
				username: newProfile.personalInfo
					? `${newProfile.personalInfo.name} ${newProfile.personalInfo.surname}`
					: undefined,
				ip_address: import.meta.server ? reqHeaders['x-forwarded-for'] : undefined,
			});
		},
		{deep: true, immediate: true},
	);

	async function fetchProfile() {
		if (profile.value) {
			hypermarketsStore.setHypermarketFromProfile(profile.value);
			return profile.value;
		}

		try {
			const response = await GsoaCustomerService.getMyProfile({});

			if (response) {
				profile.value = response;
				hypermarketsStore.setHypermarketFromProfile(profile.value);
			}

			return response ?? null;
		} catch (e: any) {
			throw createError({
				message: 'Failed to fetch customer profile',
				cause: e,
			});
		}
	}

	async function fetchReceipts() {
		if (receipts.value) {
			return receipts.value;
		}

		try {
			const response = await GsoaReceiptsService.getMyReceipts({includeGlobusOnline: true});

			if (response) {
				receipts.value = response;
			}

			return response ?? null;
		} catch (e: any) {
			throw createError({
				message: 'Failed to fetch receipts',
				cause: e,
			});
		}
	}

	async function updateProfile(newProfile: CustomerUpdateParams) {
		try {
			const response = await GsoaCustomerService.updateMyProfile({requestBody: newProfile});

			if (response) {
				profile.value = response;
			}

			return response;
		} catch (e: unknown) {
			throw createError({
				message: 'Failed to update customer profile',
				cause: e,
			});
		}
	}

	function setProfile(newProfile: CRMGCustomer | null) {
		profile.value = newProfile;
	}

	let cancelablePromise: CancelablePromise<GetCustomerCoupons> | null = null;

	function fetchAllCoupons() {
		if (!userIsLogged || !profile.value?.registration?.customerID) {
			return Promise.resolve(null);
		}

		return cache.fetchItems('coupons', async () => {
			cancelablePromise?.cancel?.();

			cancelablePromise = GsoaCouponsService.getCustomerCoupons({
				customerId: Number(profile.value?.registration?.customerID),
				pageSize: 200,
			});

			const response = await cancelTimeout(() => Promise.resolve(cancelablePromise));

			coupons.value = {
				...response,
				totalCount: response?.totalCount || 0,
			};

			return response;
		});
	}

	async function fetchAllGifts() {
		if (allGifts.value) {
			return allGifts.value;
		}

		try {
			const response = await GsoaCouponsService.couponsGetAllCouponsCustomer({});

			if (response) {
				allGifts.value = response;
			}

			return response;
		} catch (e: any) {
			throw createError({
				message: 'Failed to fetch gifts',
				cause: e,
			});
		}
	}

	async function fetchCoupon(couponId: number): Promise<void> {
		if (coupons.value?.List?.find((item) => item.Id === couponId)) {
			return;
		}

		const response = profile.value?.registration?.customerID
			? await cancelTimeout(() => GsoaCouponsService.getCustomerCoupons({
				filter: `Id:eq ${couponId}`,
				customerId: Number(profile.value?.registration?.customerID),
			}))
			: null;

		// Add the coupon from the response to the coupons object, if the coupons object doesn't exist initialize it.
		if (response?.List?.length) {
			if (coupons.value?.List) {
				coupons.value.List.push(response.List[0]);
			} else {
				coupons.value = {
					List: [response.List[0]],
					totalCount: 1,
				};
			}
		}
	}

	function upsertCoupon(coupon: CustomerCoupon): void {
		if (coupons.value?.List) {
			const couponIndex = coupons.value.List.findIndex((item) => item.Id === coupon.Id);

			if (couponIndex >= 0) {
				coupons.value.List[couponIndex] = coupon;
			} else {
				coupons.value.List.push(coupon);
				coupons.value.totalCount += 1;
			}
		} else {
			coupons.value = {
				List: [coupon],
				totalCount: 1,
			};
		}
	}

	function normalizePositiveInteger(value: unknown): number | null {
		const numberValue = Number(value);
		return Number.isInteger(numberValue) && numberValue > 0 ? numberValue : null;
	}

	function getCouponCustomerId(coupon: CustomerCoupon | undefined): number | null {
		return normalizePositiveInteger(coupon?.CouponCustomerId);
	}

	function customerCouponMatchesGift(customerCoupon: CustomerCoupon, gift: GiftWithType): boolean {
		const giftCouponId = gift.coupon.Id;
		const hasOriginalRebateId = customerCoupon.OriginalRebateIds?.some((id) => Number(id) === giftCouponId) === true;

		return customerCoupon.Id === giftCouponId
			|| customerCoupon.OriginalRebateId === giftCouponId
			|| hasOriginalRebateId
			|| (!!customerCoupon.EAN && customerCoupon.EAN === gift.coupon.EAN);
	}

	function getGiftActivationCustomerCouponFromList(gift: GiftWithType, customerCoupons: CustomerCoupon[] | undefined): CustomerCoupon | undefined {
		return customerCoupons?.find((coupon) => customerCouponMatchesGift(coupon, gift) && getCouponCustomerId(coupon));
	}

	function upsertCoupons(newCoupons: CustomerCoupon[] | undefined): void {
		newCoupons?.forEach(upsertCoupon);
	}

	async function fetchGiftActivationCustomerCoupons(customerId: number, gift: GiftWithType): Promise<CustomerCoupon[]> {
		const filters = [
			`Id:eq ${gift.coupon.Id}`,
			`OriginalRebateId:eq ${gift.coupon.Id}`,
		];

		for (const filter of filters) {
			const response = await cancelTimeout(() => GsoaCouponsService.getCustomerCoupons({
				filter,
				customerId,
				pageSize: 5,
			}));

			upsertCoupons(response?.List);

			const matchingCoupon = response?.List?.find((coupon) => customerCouponMatchesGift(coupon, gift));

			if (matchingCoupon && getCouponCustomerId(matchingCoupon)) {
				return [matchingCoupon];
			}
		}

		const response = await cancelTimeout(() => GsoaCouponsService.getCustomerCoupons({
			customerId,
			pageSize: 200,
		}));

		upsertCoupons(response?.List);

		return response?.List ?? [];
	}

	async function getGiftActivationCustomerCoupon(gift: GiftWithType): Promise<CustomerCoupon | null> {
		const cachedCustomerCoupon = getGiftActivationCustomerCouponFromList(gift, coupons.value?.List);

		if (cachedCustomerCoupon) {
			return cachedCustomerCoupon;
		}

		const customerId = Number(profile.value?.registration?.customerID);

		if (!customerId) {
			return null;
		}

		const customerCoupons = await fetchGiftActivationCustomerCoupons(customerId, gift);
		return getGiftActivationCustomerCouponFromList(gift, customerCoupons) ?? null;
	}

	function setCustomerCouponActivated(couponId: number, activated: boolean): void {
		coupons.value?.List?.forEach((coupon) => {
			if (coupon.Id === couponId) {
				coupon.Activated = activated;
			}
		});
	}

	function getGiftLevelForCoupon(couponId: number, authorize = true): GiftLevel | undefined {
		const levels = authorize ? giftLevels : giftLevelsNoAuthorization;
		return levels.value?.find(({couponId: id}) => id === couponId);
	}

	async function fetchGiftLevels() {
		const allGiftsHaveLevel = allGifts.value?.pending?.every(({coupon}) => !!getGiftLevelForCoupon(coupon.Id));

		if (giftLevels.value && isAuthenticated.value === giftLevelsFetchedAuthorized.value && allGiftsHaveLevel) {
			return giftLevels.value;
		}

		try {
			const response = await cancelTimeout(() => GsoaCustomerService.getAllGiftLevels({}));

			giftLevels.value = response;
			giftLevelsFetchedAuthorized.value = isAuthenticated.value;

			return response;
		} catch (e: any) {
			throw createError({
				message: 'Failed to fetch gift levels',
				cause: e,
			});
		}
	}

	async function fetchGiftLevelsNoAuthorization() {
		try {
			const response = await cancelTimeout(() => GsoaCustomerService.getAllGiftLevels({}));
			giftLevelsNoAuthorization.value = response;

			return response;
		} catch (e: any) {
			throw createError({
				message: 'Failed to fetch gift levels for coupons without authorization',
				cause: e,
			});
		}
	}

	async function activateCoupon(id: number, couponCustomerId: number): Promise<void> {
		if (!userIsLogged) {
			return;
		}

		const response = await cancelTimeout(() => GsoaCouponsService.setCustomerCouponActivity({
			customerId: Number(profile.value?.registration?.customerID),
			couponId: id,
			requestBody: {
				...(couponCustomerId && {CouponCustomerId: couponCustomerId}),
				Activity: true,
			},
		}));

		if (response && response.CouponId) {
			coupons.value?.List?.filter((item) => {
				if (item.Id === response.CouponId) {
					item.Activated = response.Activated;
					return true;
				}
				return false;
			});
		}
	}

	async function activateGift(gift: GiftWithType): Promise<void> {
		if (!userIsLogged.value) {
			return;
		}

		const activationCustomerCoupon = await getGiftActivationCustomerCoupon(gift);
		const couponCustomerId = getCouponCustomerId(activationCustomerCoupon ?? undefined);
		const activationCouponId = normalizePositiveInteger(activationCustomerCoupon?.Id);

		if (!activationCouponId || !couponCustomerId) {
			throw createError({message: `Missing CouponCustomerId for gift coupon ${gift.coupon.Id}`});
		}

		const response = await cancelTimeout(() => GsoaCouponsService.setCustomerCouponActivity({
			customerId: Number(profile.value?.registration?.customerID),
			couponId: activationCouponId,
			requestBody: {
				CouponCustomerId: couponCustomerId,
				Activity: true,
			},
		}));

		setCustomerCouponActivated(response?.CouponId ?? activationCouponId, true);
		setGiftActivated(gift.coupon.Id, true);
	}

	function setGiftActivated(couponId: number, activated: boolean): void {
		const giftGroups = [
			allGifts.value?.active,
			allGifts.value?.pending,
			allGifts.value?.used,
			allGifts.value?.lastQuarter?.active,
			allGifts.value?.lastQuarter?.used,
		];

		giftGroups.forEach((gifts) => {
			gifts?.forEach((gift) => {
				if (gift.coupon.Id === couponId) {
					gift.coupon.Activated = activated;
				}
			});
		});
	}

	async function activateAllCoupons(couponsToActivate: SetCouponCustomer[]) {
		try {
			if (!userIsLogged) {
				return;
			}

			const response = await cancelTimeout(() => GsoaCouponsService.setCustomerCouponActivityBatch({
				customerId: Number(profile.value?.registration?.customerID),
				requestBody: {SetActivityValue: true, CouponsToProcess: couponsToActivate},
			}));
			if (response && response.SuccessfullyProcessed) {
				response.SuccessfullyProcessed.forEach((updatedCoupon) => {
					coupons.value?.List?.filter((coupon) => {
						if (coupon.Id === updatedCoupon.CouponId) {
							coupon.Activated = true;
							return true;
						}
						return false;
					});
				});
			}
		} catch (e: any) {
			console.log(e);
			return e;
		}
	}

	function clear() {
		profile.value = null;
		receipts.value = null;
		coupons.value = null;
		allGifts.value = null;
		giftLevels.value = null;
		giftLevelsNoAuthorization.value = null;
		giftLevelsFetchedAuthorized.value = false;
	}

	return {
		profile,
		fetchProfile,
		receipts,
		fetchReceipts,
		updateProfile,
		fetchAllCoupons,
		coupons,
		activateCoupon,
		activateGift,
		activateAllCoupons,
		userIsLogged,
		fetchCoupon,
		fetchAllGifts,
		allGifts,
		fetchGiftLevels,
		fetchGiftLevelsNoAuthorization,
		giftLevels,
		giftLevelsNoAuthorization,
		getGiftLevelForCoupon,
		flatGifts,
		giftsWithType,
		filteredCoupons,
		filteredCouponsCount,
		couponsAndGiftsCount,
		clear,
		setProfile,
	};
});
